import partial from 'lodash/fp/partial';
import { FormattedMessage } from 'react-intl';

import { Chapter } from './Chapter';
import { InfoDialogMenuItem } from './InfoDialogMenuItem';
import ListMenu from '@rio-cloud/rio-uikit/ListMenu';
import { useAppSelector } from '../../../configuration/setup/hooks';
import { accountType, type AccountType, getAccountType } from '../../../configuration/login/loginSlice';
import type { ListMenuNavItem } from '@rio-cloud/rio-uikit/components/listMenu/ListMenuGroup';

interface ExtendedNavItem extends ListMenuNavItem {
    applicableAccountTypes: AccountType[];
}

const ALL_ACCOUNT_TYPES: AccountType[] = [accountType.CARRIER, accountType.BUYER, accountType.SUPPLIER];

const buildNavItem = (
    activeChapter: string,
    // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience for migration to biomejs
    onClick: (event: any) => void,
    applicableAccountTypes: AccountType[],
    props: { chapter: string }
): ExtendedNavItem => ({
    key: props.chapter,
    item: <InfoDialogMenuItem activeChapter={activeChapter} onClick={onClick} {...props} />,
    applicableAccountTypes,
});

// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience for migration to biomejs
const getMenuItems = (activeChapter: string, onClick: (event: any) => void, currentAccountType: AccountType) => {
    const buildItem = partial(buildNavItem, [activeChapter, onClick]);

    const isApplicableNavItems = (navItem: ExtendedNavItem) =>
        navItem.applicableAccountTypes.includes(currentAccountType);

    return [
        {
            group: <FormattedMessage id={'intl-msg:infoDialog.about'} />,
            navItems: [
                buildItem(ALL_ACCOUNT_TYPES, {
                    chapter: Chapter.WELCOME,
                }),
            ].filter(isApplicableNavItems),
        },
        {
            group: <FormattedMessage id={'intl-msg:infoDialog.concepts'} />,
            navItems: [
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.GROUPS,
                }),
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.ROLES,
                }),
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.FLEET,
                }),

                buildItem([accountType.BUYER], {
                    chapter: Chapter.BUYER_GROUPS,
                }),
                buildItem([accountType.SUPPLIER], {
                    chapter: Chapter.SUPPLIER_GROUPS,
                }),
            ].filter(isApplicableNavItems),
        },
        {
            group: <FormattedMessage id={'intl-msg:infoDialog.accountSettings'} />,
            navItems: [
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.DRIVER_SELF_REGISTRATION,
                }),
            ].filter(isApplicableNavItems),
        },
        {
            group: <FormattedMessage id={'intl-msg:infoDialog.features'} />,
            navItems: [
                buildItem(ALL_ACCOUNT_TYPES, {
                    chapter: Chapter.INVITE_USER,
                }),
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.INVITE_USER_FOR_DRIVER,
                }),
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.CREATE_USER_GROUP,
                }),
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.RESTRICT_USER_GROUP,
                }),
                buildItem(ALL_ACCOUNT_TYPES, {
                    chapter: Chapter.DELETE_USER,
                }),
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.DELETE_USER_GROUP,
                }),
                buildItem([accountType.CARRIER], {
                    chapter: Chapter.ONBOARD_EU_DATA_ACT,
                }),
                buildItem(ALL_ACCOUNT_TYPES, {
                    chapter: Chapter.SUPPORTED_PHONE_COUNTRY_CODES,
                }),
            ].filter(isApplicableNavItems),
        },
    ];
};

interface Props {
    chapter: string;
    // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience for migration to biomejs
    onSelectChapter: (event: any) => void;
}

export const InfoDialogMenu = ({ chapter, onSelectChapter }: Props) => {
    const accountType = useAppSelector(getAccountType);

    return (
        <ListMenu menuItems={getMenuItems(chapter, onSelectChapter, accountType)} groupClassName={'padding-left-0'} />
    );
};
